// The project wiki panel (Brief 5) — the second brain Signal builds, on the
// desktop. Renders the canonical sections (goals/rules/guidelines/repos), each
// in one of three honest states: FILLED (markdown + when), ASKING-NOW (the
// section of the open question — the one amber accent), or NOT-YET ("Signal will
// ask"). Markdown via react-markdown (no raw HTML — its default — so a section's
// text can never inject markup). Token-pure via the channels.css `.wt-wiki*`
// classes; the prose (incl. code) is styled there, off the design tokens.

import Markdown from "react-markdown";
import type { BrainQuestion, WikiSection } from "@waytide/core";

/** The sections Signal builds, in reading order. Matches the orchestrator's
 * question sequence; the backend sorts the same way. */
const CANON: { key: string; title: string }[] = [
  { key: "goals", title: "Goals" },
  { key: "rules", title: "Rules" },
  { key: "guidelines", title: "Guidelines" },
  { key: "repos", title: "Repos" },
];

/** A calm relative time for a section's last write. */
export function whenLabel(micros: number, nowMs: number): string {
  if (!micros) return "";
  const secs = Math.max(0, Math.floor(nowMs / 1000 - micros / 1_000_000));
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export function WikiPanel({
  sections,
  questions,
  pendingSection,
}: {
  sections: WikiSection[];
  questions: BrainQuestion[];
  pendingSection: string | null;
}) {
  const bySection = new Map(sections.map((s) => [s.section, s]));
  const askedSections = new Set(questions.map((q) => q.section));
  const now = Date.now();
  // Future-proof: any section the server has beyond the canon renders after.
  const extra = sections.filter((s) => !CANON.some((c) => c.key === s.section));

  return (
    <div className="wt-wiki" data-testid="wiki-panel">
      <div className="wt-wiki__head">Project brain</div>
      {CANON.map(({ key, title }) => {
        const doc = bySection.get(key);
        if (doc) {
          const when = whenLabel(doc.updated_at, now);
          return (
            <section key={key} className="wt-wiki__section">
              <h2 className="wt-wiki__title">{title}</h2>
              <div className="wt-wiki__body">
                <Markdown>{doc.markdown}</Markdown>
              </div>
              {when ? <p className="wt-wiki__meta">updated {when}</p> : null}
            </section>
          );
        }
        const asking = pendingSection === key;
        const answered = askedSections.has(key); // asked, awaiting synthesis
        return (
          <section
            key={key}
            className={`wt-wiki__section wt-wiki__section--ghost${
              asking ? " wt-wiki__section--asking" : ""
            }`}
          >
            <h2 className="wt-wiki__title">{title}</h2>
            <p className="wt-wiki__placeholder">
              {asking
                ? "Signal is asking you about this now — answer in the chat."
                : answered
                  ? "Signal is filling this in…"
                  : "Signal will ask about this."}
            </p>
          </section>
        );
      })}
      {extra.map((s) => (
        <section key={s.section} className="wt-wiki__section">
          <h2 className="wt-wiki__title">{s.title}</h2>
          <div className="wt-wiki__body">
            <Markdown>{s.markdown}</Markdown>
          </div>
        </section>
      ))}
    </div>
  );
}
